import React, { useState, useEffect } from "react";
import {
  Stack,
  TextField,
  MenuItem,
  Box,
  Typography,
  Slider,
  useTheme,
  FormControl,
  FormHelperText,
  Grid,
  alpha,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { PLATFORMS } from "../constants";
import { useUnits } from "../../../contexts/UnitContext";

// Glassmorphic container for the entire step
const GlassStepContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(4),
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.95)} 0%, 
    ${alpha(theme.palette.background.paper, 0.88)} 50%,
    ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
  backdropFilter: "blur(25px) saturate(1.1)",
  WebkitBackdropFilter: "blur(25px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 24,
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.6)}, 
      ${alpha(theme.palette.secondary.main, 0.4)}, 
      transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 15% 25%, 
      ${alpha(theme.palette.primary.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 85% 75%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Enhanced glassmorphic text field
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.8)} 0%, 
      ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    borderRadius: 16,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, 
        transparent, 
        ${alpha(theme.palette.common.white, 0.3)}, 
        transparent)`,
      borderRadius: "16px 16px 0 0",
    },

    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.85)} 0%, 
        ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
      transform: "translateY(-1px)",
      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.08)}`,
    },

    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
      transform: "translateY(-2px)",
      boxShadow: `0 12px 35px ${alpha(theme.palette.primary.main, 0.12)}`,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& .MuiInputBase-input": {
      padding: theme.spacing(1.5),
      fontSize: "1rem",
      fontWeight: 400,
      "&::placeholder": {
        color: alpha(theme.palette.text.secondary, 0.7),
        opacity: 1,
      },
    },

    "&.MuiInputBase-multiline": {
      "& .MuiInputBase-input": {
        padding: theme.spacing(1.5),
      },
    },
  },

  "& .MuiInputLabel-root": {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: "1rem",
    transform: "translate(14px, 12px) scale(1)",

    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.85)",
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
      padding: "0 8px",
      borderRadius: 8,
      backdropFilter: "blur(10px)",
    },

    "&.Mui-focused": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },

  "& .MuiFormHelperText-root": {
    color: alpha(theme.palette.text.secondary, 0.8),
    fontSize: "0.875rem",
    fontWeight: 400,
    marginTop: theme.spacing(1),
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.6)} 0%, 
      ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
    backdropFilter: "blur(8px)",
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    display: "inline-block",
  },
}));

// Glassmorphic MenuItem with hover effects
const GlassMenuItem = styled(MenuItem)(({ theme }) => ({
  background: "transparent",
  transition: "all 0.2s ease",
  borderRadius: 8,
  margin: theme.spacing(0.25, 0.5),

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.12
    )} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    transform: "translateX(4px)",
  },

  "&.Mui-selected": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.18
    )} 0%, ${alpha(theme.palette.primary.main, 0.09)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    color: theme.palette.primary.main,
    fontWeight: 600,

    "&:hover": {
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.22
      )} 0%, ${alpha(theme.palette.primary.main, 0.11)} 100%)`,
    },
  },
}));

// Enhanced depth control container
const GlassDepthContainer = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.08)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.06)} 50%,
    ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 20,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.4)}, 
      transparent)`,
  },
}));

// Enhanced slider styling
const GlassSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: 8,
  padding: theme.spacing(2, 0),

  "& .MuiSlider-track": {
    background: `linear-gradient(90deg, 
      ${theme.palette.primary.main}, 
      ${theme.palette.secondary.main})`,
    border: "none",
    height: 8,
    borderRadius: 4,
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
  },

  "& .MuiSlider-rail": {
    background: alpha(theme.palette.text.secondary, 0.15),
    height: 8,
    borderRadius: 4,
    backdropFilter: "blur(10px)",
    opacity: 1,
  },

  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    background: `linear-gradient(135deg, 
      ${theme.palette.primary.main} 0%, 
      ${theme.palette.secondary.main} 100%)`,
    border: `3px solid ${alpha(theme.palette.common.white, 0.8)}`,
    boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

    "&::before": {
      content: '""',
      position: "absolute",
      width: "100%",
      height: "100%",
      background: `radial-gradient(circle, 
        ${alpha(theme.palette.common.white, 0.2)} 0%, 
        transparent 70%)`,
      borderRadius: "50%",
    },

    "&:hover": {
      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
      transform: "scale(1.1)",
    },

    "&.Mui-focusVisible": {
      boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.16)}`,
    },

    "&.Mui-active": {
      boxShadow: `0 0 0 14px ${alpha(theme.palette.primary.main, 0.16)}`,
      transform: "scale(1.2)",
    },
  },

  "& .MuiSlider-valueLabel": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.9)} 0%, 
      ${alpha(theme.palette.secondary.main, 0.8)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
    borderRadius: 12,
    padding: theme.spacing(0.5, 1),
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.common.white,

    "&::before": {
      border: `6px solid ${alpha(theme.palette.primary.main, 0.9)}`,
      borderTopColor: "transparent",
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
    },
  },
}));

// Enhanced depth input field
const GlassDepthInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.9)} 0%, 
      ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
    backdropFilter: "blur(12px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRadius: 12,
    height: 48,

    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      transform: "translateY(-1px)",
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
    },

    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& .MuiInputBase-input": {
      padding: theme.spacing(1.5),
      fontSize: "1rem",
      fontWeight: 500,
      textAlign: "center",
    },

    "& .MuiInputAdornment-root": {
      "& .MuiTypography-root": {
        fontSize: "0.875rem",
        fontWeight: 600,
        color: theme.palette.primary.main,
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.1)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        padding: theme.spacing(0.5, 1),
        borderRadius: 6,
      },
    },
  },

  "& .MuiInputLabel-root": {
    color: theme.palette.text.primary,
    fontWeight: 500,

    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
}));

const DiveDetailsStep = ({ formState, handleInputChange, setFormState }) => {
  const theme = useTheme();
  const { unitSystem } = useUnits(); // Removed `convert` as it's not directly used here

  // Maximum value for the slider in meters (50m)
  const maxMeters = 50;
  // Calculate max value for the slider based on unit system
  const sliderMaxValue =
    unitSystem === "metric" ? maxMeters : Math.round(maxMeters * 3.28084);

  // Convert stored depthRange (in meters) to display units
  const displayRange = [
    unitSystem === "metric"
      ? formState.depthRange[0]
      : formState.depthRange[0] * 3.28084,
    unitSystem === "metric"
      ? formState.depthRange[1]
      : formState.depthRange[1] * 3.28084,
  ];

  // State to manage text input values for min/max depth
  const [textValues, setTextValues] = useState({
    min: Math.round(displayRange[0] || 0), // Ensure initial values are numbers
    max: Math.round(displayRange[1] || 0),
  });

  // Effect to update text input values when formState.depthRange or unitSystem changes
  useEffect(() => {
    setTextValues({
      min: Math.round(displayRange[0]),
      max: Math.round(displayRange[1]),
    });
  }, [displayRange]); // Depend on displayRange to trigger updates when units change

  // Helper function to get display value and unit for depth
  const getDisplayDepth = (meters) => {
    if (unitSystem === "metric") {
      return {
        value: meters,
        unit: "m",
      };
    } else {
      const feet = meters * 3.28084;
      return {
        value: feet,
        unit: "ft",
      };
    }
  };

  // Handle changes from the Slider component
  const handleDepthRangeChange = (_, value) => {
    // Update text input values immediately for visual feedback
    setTextValues({
      min: Math.round(value[0]),
      max: Math.round(value[1]),
    });

    // Convert to meters before updating formState
    if (unitSystem === "metric") {
      setFormState((prev) => ({ ...prev, depthRange: value }));
    } else {
      const metersValue = [value[0] / 3.28084, value[1] / 3.28084];
      // Round to one decimal place for meters to prevent floating point issues
      setFormState((prev) => ({
        ...prev,
        depthRange: metersValue.map((v) => Math.round(v * 10) / 10),
      }));
    }
  };

  // Handle changes from the text input fields
  const handleTextChange = (type) => (event) => {
    let value = parseInt(event.target.value, 10);

    if (isNaN(value)) {
      // If input is cleared or invalid, set a default or just return
      value = 0; // Or keep the previous valid value for better UX
    }

    let newMin = type === "min" ? value : textValues.min;
    let newMax = type === "max" ? value : textValues.max;

    // Ensure min does not exceed max, and max does not fall below min
    if (newMin > newMax) {
      if (type === "min") {
        newMax = newMin;
      } else {
        newMin = newMax;
      }
    }

    // Clamp values to the overall min (0) and max (sliderMaxValue)
    newMin = Math.max(0, Math.min(newMin, sliderMaxValue));
    newMax = Math.max(0, Math.min(newMax, sliderMaxValue));

    // Update internal text input state
    setTextValues({
      min: newMin,
      max: newMax,
    });

    const newDisplayRange = [newMin, newMax];

    // Convert to meters before updating formState
    if (unitSystem === "metric") {
      setFormState((prev) => ({ ...prev, depthRange: newDisplayRange }));
    } else {
      const metersValue = [
        newDisplayRange[0] / 3.28084,
        newDisplayRange[1] / 3.28084,
      ];
      setFormState((prev) => ({
        ...prev,
        depthRange: metersValue.map((v) => Math.round(v * 10) / 10),
      }));
    }
  };

  // Get current min/max depths for display and unit label
  const minDepthDisplay = getDisplayDepth(formState.depthRange[0] || 0);
  const maxDepthDisplay = getDisplayDepth(formState.depthRange[1] || 0);

  return (
    <GlassStepContainer spacing={3}>
      <GlassTextField
        label="Divesite Description"
        name="divesitePara"
        value={formState.divesitePara}
        onChange={handleInputChange}
        multiline
        rows={4}
        fullWidth
        helperText="Paint a picture that makes divers want to explore this site! Describe what makes this site special, unique features to look for, and the marine life divers might encounter."
      />

      <GlassTextField
        label="How to dive it"
        name="diveDescription"
        value={formState.diveDescription}
        onChange={handleInputChange}
        multiline
        rows={4}
        fullWidth
        helperText="Provide clear, concise directions for diving this site successfully. Include entry/exit points, navigation tips, and any safety considerations divers should know."
      />

      <GlassTextField
        select
        label="Dive platform"
        name="platform"
        value={formState.platform}
        onChange={handleInputChange}
        helperText="Please select dive platform."
        fullWidth
        required
        SelectProps={{
          MenuProps: {
            PaperProps: {
              sx: {
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.background.paper, 0.95)} 0%, 
                  ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
                backdropFilter: "blur(20px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                borderRadius: 2,
                boxShadow: `0 8px 32px ${alpha(
                  theme.palette.common.black,
                  0.15
                )}`,
                "& .MuiList-root": {
                  padding: theme.spacing(0.5),
                },
              },
            },
          },
        }}
      >
        {PLATFORMS.map((option) => (
          <GlassMenuItem key={option.value} value={option.value}>
            {option.label}
          </GlassMenuItem>
        ))}
      </GlassTextField>

      <GlassDepthContainer elevation={0}>
        <FormControl fullWidth>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              textAlign: "center",
              mb: 3,
              background: `linear-gradient(135deg, 
                ${theme.palette.primary.main}, 
                ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Depth Range: {Math.round(displayRange[0])} -{" "}
            {Math.round(displayRange[1])} {minDepthDisplay.unit}
          </Typography>

          <GlassSlider
            value={displayRange}
            onChange={handleDepthRangeChange}
            valueLabelDisplay="auto"
            min={0}
            max={sliderMaxValue}
            valueLabelFormat={(value) =>
              `${Math.round(value)} ${minDepthDisplay.unit}`
            }
          />

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <GlassDepthInput
                label="Min Depth"
                type="number"
                value={textValues.min}
                onChange={handleTextChange("min")}
                InputProps={{
                  endAdornment: (
                    <Typography variant="body2">
                      {minDepthDisplay.unit}
                    </Typography>
                  ),
                }}
                inputProps={{
                  min: 0,
                  max: sliderMaxValue,
                  step: 1,
                }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <GlassDepthInput
                label="Max Depth"
                type="number"
                value={textValues.max}
                onChange={handleTextChange("max")}
                InputProps={{
                  endAdornment: (
                    <Typography variant="body2">
                      {maxDepthDisplay.unit}
                    </Typography>
                  ),
                }}
                inputProps={{
                  min: 0,
                  max: sliderMaxValue,
                  step: 1,
                }}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <FormHelperText
            sx={{
              mt: 2,
              fontSize: "0.875rem",
              color: alpha(theme.palette.text.secondary, 0.8),
              background: `linear-gradient(135deg, 
                ${alpha(theme.palette.background.paper, 0.6)} 0%, 
                ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
              backdropFilter: "blur(8px)",
              padding: theme.spacing(0.5, 1),
              borderRadius: 8,
              display: "inline-block",
              textAlign: "center",
              width: "100%",
            }}
          >
            {unitSystem === "metric"
              ? "Depth in meters"
              : "Depth in feet (will be stored as meters)"}
          </FormHelperText>
        </FormControl>
      </GlassDepthContainer>
    </GlassStepContainer>
  );
};

export default DiveDetailsStep;
